import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Tag, TextField, Button } from "@ann/web-components";
import {
  TagsSection,
  AddTagsButtonTop,
  TagsInputContainer,
  TagsInputRow,
  InputWrapper,
  ButtonsWrapper,
  TooltipTrigger,
  TagsRow,
  TagsItems,
  TagsActions,
  AddTagsButtonBottom,
} from "./DocumentTagsSection.styled";
import {
  TAG_COLORS,
  calculateVisibleTags,
  tagExists,
  getTagColor,
  findInputElement,
  formatTagsForTooltip,
} from "../../../../utils";

const DocumentTagsSection = ({ tags = [], onAddTag, onRemoveTag }) => {
  const [isInputOpen, setIsInputOpen] = useState(false);
  const [newTagText, setNewTagText] = useState("");
  const inputWrapperRef = useRef(null);

  const hasTags = tags.length > 0;

  const { visibleTags, hiddenTagsCount, hiddenTags } = useMemo(
    () => calculateVisibleTags(tags),
    [tags]
  );

  const handleOpenInput = useCallback(() => {
    setIsInputOpen(true);
    setNewTagText("");
  }, []);

  const handleCloseInput = useCallback(() => {
    setIsInputOpen(false);
    setNewTagText("");
  }, []);

  const handleAddTag = useCallback(() => {
    const trimmedTag = newTagText.trim();
    if (!trimmedTag || tagExists(trimmedTag, tags)) return;

    onAddTag?.(trimmedTag);
    setNewTagText("");
    setIsInputOpen(false);
  }, [newTagText, tags, onAddTag]);

  useEffect(() => {
    if (!isInputOpen || !inputWrapperRef.current) return;

    const inputElement = findInputElement(inputWrapperRef.current);
    if (!inputElement) return;

    const handleKeyDownEvent = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        handleAddTag();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCloseInput();
      }
    };

    inputElement.addEventListener("keydown", handleKeyDownEvent, true);
    return () => {
      inputElement.removeEventListener("keydown", handleKeyDownEvent, true);
    };
  }, [isInputOpen, handleAddTag, handleCloseInput]);

  const handleRemoveTag = useCallback(
    (tagToRemove) => {
      onRemoveTag?.(tagToRemove);
    },
    [onRemoveTag]
  );

  return (
    <TagsSection>
      {!hasTags && !isInputOpen && (
        <AddTagsButtonTop>
          <Button
            label="+ Add tags"
            type="tertiary"
            size="medium"
            onClick={handleOpenInput}
          />
        </AddTagsButtonTop>
      )}

      {isInputOpen && (
        <TagsInputContainer>
          <TagsInputRow>
            <InputWrapper ref={inputWrapperRef}>
              <TextField
                placeholder="Write the tag name"
                value={newTagText}
                onChange={setNewTagText}
                size="small"
                autoFocus
              />
            </InputWrapper>
            <ButtonsWrapper>
              <Button
                label="Add tag"
                type="primary"
                size="medium"
                onClick={handleAddTag}
                disabled={!newTagText.trim()}
              />
              <Button
                label="Close"
                type="tertiary"
                size="medium"
                onClick={handleCloseInput}
              />
            </ButtonsWrapper>
          </TagsInputRow>
        </TagsInputContainer>
      )}

      {hasTags && (
        <TagsRow>
          <TagsItems>
            {visibleTags.map((tag, index) => (
              <Tag
                key={`${tag}-${index}`}
                text={tag}
                color={getTagColor(index, TAG_COLORS)}
                size="medium"
                closable
                onClose={() => handleRemoveTag(tag)}
              />
            ))}

            {hiddenTagsCount > 0 && (
              <TooltipTrigger>
                <Tag
                text={`+ ${hiddenTagsCount}`}
                color="secondary"
                size="medium"
                tooltip={formatTagsForTooltip(hiddenTags)}
              />
              </TooltipTrigger>
            )}
          </TagsItems>
          {!isInputOpen && (
            <TagsActions>
              <AddTagsButtonBottom>
                <Button
                  label="+ Add tags"
                  type="tertiary"
                  size="medium"
                  onClick={handleOpenInput}
                />
              </AddTagsButtonBottom>
            </TagsActions>
          )}
        </TagsRow>
      )}
    </TagsSection>
  );
};

export default DocumentTagsSection;
